import { useCallback, useEffect, useRef } from 'react';
import { Subject } from 'rxjs/Subject';
import 'rxjs/add/operator/buffer';
import type { RealtimeEvent } from '@atlassian/jira-realtime';
import { REFRESH_SOURCE_REALTIME } from '../../model/constants';
import { useBacklogActionCreator, useBacklogSelector } from '../../state';
import { refreshDataRequest } from '../../state/actions/app';
import { dispatchRealtimeEventBatch } from '../../state/actions/realtime/dispatch';
import { getAtlassianAccountId } from '../../state/selectors/app';

/* Currently both FF below have targetting off and default to 500ms.
    jsw.perf.custom-filters-core-tmp.debounce-time
    jsw.nextgen.custom-filters.refiltering-debounce-interval-ms

    Given the FFs were created > 2 years ago and not rolled-out
    at all, hard coding the debounce time to 500ms for now.
 */
export const DEBOUNCE_TIME_IN_MS = 500;
const isActiveTab = () => !document.hidden;

export const useBatchOnReceive = () => {
	const subjectRef = useRef(new Subject<RealtimeEvent>());
	const currentUser = useBacklogSelector(getAtlassianAccountId);
	const refreshData = useBacklogActionCreator(refreshDataRequest);
	const dispatch = useBacklogActionCreator(dispatchRealtimeEventBatch);

	const pushEvent = useCallback((event: RealtimeEvent) => {
		if (!subjectRef.current.closed) {
			subjectRef.current.next(event);
		}
	}, []);

	useEffect(() => {
		const subject = subjectRef.current;
		subject
			.buffer(subject.debounceTime(DEBOUNCE_TIME_IN_MS))
			.filter(isActiveTab)
			.subscribe((events) => {
				if (!subjectRef.current.closed) {
					dispatch(events);

					const hasEventsFromOthers =
						events.filter((s) => s.payload.atlassianId !== currentUser).length > 0;
					if (hasEventsFromOthers) {
						refreshData(REFRESH_SOURCE_REALTIME);
					}
				}
			});
		return () => {
			subject.unsubscribe();
		};
	}, [currentUser, dispatch, refreshData]);

	return pushEvent;
};
